/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  AppBar,
  Toolbar,
  Container,
  Paper,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import ScheduleIcon from '@mui/icons-material/Schedule';
import axiosInstance from '../../helper/AxiosInstance';

const JudgeDashboard = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [presentations, setPresentations] = useState({ day1: [], day2: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openScoreDialog, setOpenScoreDialog] = useState(false);
  const [selectedPresentation, setSelectedPresentation] = useState(null);
  const [scores, setScores] = useState({
    businessAndEngineeringContext: 0,
    complexityOfTechnicalSolutions: 0,
    interpretationOfResultsAndConclusion: 0,
    businessValueDiscussionAndAchievement: 0,
    presentationQualityandDeliveryStyle: 0
  });

  const userData = JSON.parse(sessionStorage.getItem('userData'));

  // Verify track access
  useEffect(() => {
    if (parseInt(trackId) !== userData.userTrack) {
      console.warn(`Judge attempted to access wrong track (${trackId} vs assigned ${userData.userTrack})`);
      navigate(`/judge/track/${userData.userTrack}/dashboard`);
    }
  }, [trackId, userData.userTrack, navigate]);

  const fetchPresentations = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/scoring/fetchprasentationbyjudge?role=${userData.userRole}&track=${userData.userTrack}`);
      console.log(response.data);
      console.log(response.data.userList.forEach(item => console.log(item.id)));
      if (response.data?.userList) {
        const day1 = response.data.userList.filter(p => p.day === 1);
        const day2 = response.data.userList.filter(p => p.day === 2);
        setPresentations({ day1, day2 });
      }
    } catch (err) {
      console.error('Error fetching presentations:', err);
      setError('Failed to fetch presentations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPresentations();
  }, [userData.userRole, userData.userTrack]);

  const handleScoreSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Get the judge's role prefix (e.g., "j2" from "j2Name")
      const judgeRolePrefix = userData.userRole.toLowerCase();

      const payload = {
        [`${judgeRolePrefix}Name`]: userData.userName,
        [`${judgeRolePrefix}BusinessAndEngineeringContext`]: scores.businessAndEngineeringContext.toString(),
        [`${judgeRolePrefix}ComplexityOfTechnicalSolutions`]: scores.complexityOfTechnicalSolutions.toString(),
        [`${judgeRolePrefix}InterpretationOfResultsAndConclusion`]: scores.interpretationOfResultsAndConclusion.toString(),
        [`${judgeRolePrefix}BusinessValueDiscussionAndAchievement`]: scores.businessValueDiscussionAndAchievement.toString(),
        [`${judgeRolePrefix}PresentationQualityandDeliveryStyle`]: scores.presentationQualityandDeliveryStyle.toString(),
      };

      const response = await axiosInstance.post(
        `/scoring/addscore?id=${selectedPresentation.id}&role=${userData.userRole}`,
        payload
      );

      if (response.data.status) {
        // Refresh presentations after successful submission
        fetchPresentations();
        setOpenScoreDialog(false);
      } else {
        setError(response.data.statusMessage || 'Failed to submit scores');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error submitting scores');
      console.error('Error submitting scores:', err);
    } finally {
      setLoading(false);
    }
  };

  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  const handleLogoutConfirm = () => {
    sessionStorage.removeItem('userData');
    navigate('/login');
  };

  const getTrackName = (trackNumber) => {
    const trackNames = {
      1: "Structures & Materials 1",
      2: "Structures & Materials 2",
      3: "Vehicle Dynamics",
      4: "System Dynamics & Machinery",
      5: "Fluids & Co-Simulation",
      6: "Virtual Manufacturing"
    };
    return trackNames[trackNumber] || `Track ${trackNumber}`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#faf9f6' }}>
      <AppBar position="sticky" sx={{ background: "linear-gradient(to right, #1976d2, #42a5f5)" }}>
        <Toolbar>
          <ScoreboardIcon sx={{ mr: 1 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Track: {getTrackName(trackId)}
          </Typography>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setOpenLogoutDialog(true)}
            startIcon={<LogoutIcon />}
            sx={{
              borderRadius: 50,
              textTransform: "none",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ p: 3, flex: 1 }}>
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "1.2rem", sm: "2rem", md: "2.8rem" },
          }}
        >
          Hello,{" "}
          <span style={{ color: "#33c263ff" }}>{userData.userName}</span>
        </Typography>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            px: 2,
            pt: 2,
            '& .MuiTab-root': {
              fontWeight: 600,
              minWidth: 'auto',
              mr: { xs: 1, sm: 2, md: 3 },
            },
          }}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
        >
          <Tab label="Day 1 Presentations" />
          <Tab label="Day 2 Presentations" />
          <Tab label="Scores" />
        </Tabs>

        {/* Reference Table for Scoring Criteria */}
        <Box sx={{ p: 4 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "1rem", mb: 2 }}>
            Scoring Criteria
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: 2, maxWidth: 600 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#cdcdcd" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Criteria</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Max Score</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Weightage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { criteria: "Business Value Discussion & Achievement", max: 10, weight: 5 },
                  { criteria: "Complexity Of Technical Solutions", max: 10, weight: 3 },
                  { criteria: "Interpretation of Results & Conclusion", max: 10, weight: 3 },
                  { criteria: "Business & Engineering Context", max: 10, weight: 2 },
                  { criteria: "Presentation Quality and Delivery Style", max: 10, weight: 3 },
                ].map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.criteria}</TableCell>
                    <TableCell align="center">{row.max}</TableCell>
                    <TableCell align="center">{row.weight}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ p: 3 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* {activeTab === 0 || activeTab === 1 ? (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#cdcdcd" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Time Slot</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Presenter</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {presentations[activeTab === 0 ? "day1" : "day2"].length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography sx={{ fontSize: 15, fontWeight: 600, color: "gray" }}>
                              No presentations scheduled for this day
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        presentations[activeTab === 0 ? "day1" : "day2"].map((presentation) => (
                          <TableRow key={presentation.id}>
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                <ScheduleIcon sx={{ mr: 1 }} />
                                {presentation.timeFrom} - {presentation.timeTo}
                              </Box>
                            </TableCell>
                            <TableCell>{presentation.title}</TableCell>
                            <TableCell>
                              {presentation.authorName}
                              <Typography variant="body2" color="text.secondary">
                                {presentation.authorEmail}
                              </Typography>
                            </TableCell>
                            <TableCell>{presentation.organization}</TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setSelectedPresentation(presentation);
                                  setScores({
                                    businessAndEngineeringContext:
                                      presentation.businessAndEngineeringContext || 0,
                                    complexityOfTechnicalSolutions:
                                      presentation.complexityOfTechnicalSolutions || 0,
                                    interpretationOfResultsAndConclusion:
                                      presentation.interpretationOfResultsAndConclusion || 0,
                                    businessValueDiscussionAndAchievement:
                                      presentation.businessValueDiscussionAndAchievement || 0,
                                    presentationQualityandDeliveryStyle:
                                      presentation.presentationQualityandDeliveryStyle || 0,
                                  });
                                  setOpenScoreDialog(true);
                                }}
                                sx={{ fontWeight: 600, borderRadius: 8, fontSize: 14 }}
                              >
                                Submit Score
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null} */}
              {(activeTab === 0 || activeTab === 1) ? (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#cdcdcd" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Time Slot</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Presenter</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {presentations[activeTab === 0 ? "day1" : "day2"].length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography sx={{ fontSize: 15, fontWeight: 600, color: "gray" }}>
                              No presentations scheduled for this day
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        presentations[activeTab === 0 ? "day1" : "day2"]
                          .slice() // copy to avoid mutating state
                          .sort((a, b) => {
                            // assumes timeFrom is in HH:mm (24hr) format
                            const timeA = new Date(`1970-01-01T${a.timeFrom}:00`);
                            const timeB = new Date(`1970-01-01T${b.timeFrom}:00`);
                            return timeA - timeB;
                          })
                          .map((presentation) => (
                            <TableRow key={presentation.id}>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  <ScheduleIcon sx={{ mr: 1 }} />
                                  {presentation.timeFrom} - {presentation.timeTo}
                                </Box>
                              </TableCell>
                              <TableCell>{presentation.title}</TableCell>
                              <TableCell>
                                {presentation.authorName}
                                <Typography variant="body2" color="text.secondary">
                                  {presentation.authorEmail}
                                </Typography>
                              </TableCell>
                              <TableCell>{presentation.organization}</TableCell>
                              <TableCell>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setSelectedPresentation(presentation);
                                    setScores({
                                      businessAndEngineeringContext:
                                        presentation.businessAndEngineeringContext || 0,
                                      complexityOfTechnicalSolutions:
                                        presentation.complexityOfTechnicalSolutions || 0,
                                      interpretationOfResultsAndConclusion:
                                        presentation.interpretationOfResultsAndConclusion || 0,
                                      businessValueDiscussionAndAchievement:
                                        presentation.businessValueDiscussionAndAchievement || 0,
                                      presentationQualityandDeliveryStyle:
                                        presentation.presentationQualityandDeliveryStyle || 0,
                                    });
                                    setOpenScoreDialog(true);
                                  }}
                                  sx={{ fontWeight: 600, borderRadius: 8, fontSize: 14 }}
                                >
                                  Submit Score
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}

              {activeTab === 2 ? (
                <>
                  {/* Day 1 Scores */}
                  <Typography sx={{ mb: 2, fontSize: 18, fontWeight: 500 }}>
                    Day - 1 Scores
                  </Typography>
                  <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3, mb: 4 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ background: "linear-gradient(45deg, #a8edea, #fed6e3)" }}>
                          <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Presenter</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Business Value (wg:x5)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Complexity Tech Solutions (wg:x3)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Interpretation of results (wg:x3)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Business & Eng. Context (wg:x2)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Presentation Quality (wg:x3)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {presentations.day1.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ fontSize: 15, fontWeight: 600, color: "gray" }}>
                              No scores found for Day 1
                            </TableCell>
                          </TableRow>
                        ) : (
                          presentations.day1.map((presentation) => (
                            <TableRow key={presentation.id}>
                              <TableCell>{presentation.title}</TableCell>
                              <TableCell>{presentation.authorName}</TableCell>
                              <TableCell>{presentation.organization}</TableCell>
                              <TableCell align='center'>{presentation.businessValueDiscussionAndAchievement || "-"}</TableCell>
                              <TableCell align='center'>{presentation.complexityOfTechnicalSolutions || "-"}</TableCell>
                              <TableCell align='center'>{presentation.interpretationOfResultsAndConclusion || "-"}</TableCell>
                              <TableCell align='center'>{presentation.businessAndEngineeringContext || "-"}</TableCell>
                              <TableCell align='center'>{presentation.presentationQualityandDeliveryStyle || "-"}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Day 2 Scores */}
                  <Typography sx={{ mb: 2, fontSize: 18, fontWeight: 500 }}>
                    Day - 2 Scores
                  </Typography>
                  <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ background: "linear-gradient(45deg, #a8edea, #fed6e3)" }}>
                          <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Presenter</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Business Value (wg:x5)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Complexity Tech Solutions (wg:x3)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Interpretation of results (wg:x3)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Business & Eng. Context (wg:x2)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Presentation Quality (wg:x3)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {presentations.day2.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ fontSize: 15, fontWeight: 600, color: "gray" }}>
                              No scores found for Day 2
                            </TableCell>
                          </TableRow>
                        ) : (
                          presentations.day2.map((presentation) => (
                            <TableRow key={presentation.id}>
                              <TableCell>{presentation.title}</TableCell>
                              <TableCell>{presentation.authorName}</TableCell>
                              <TableCell>{presentation.organization}</TableCell>
                              <TableCell align='center'>{presentation.businessValueDiscussionAndAchievement || "-"}</TableCell>
                              <TableCell align='center'>{presentation.complexityOfTechnicalSolutions || "-"}</TableCell>
                              <TableCell align='center'>{presentation.interpretationOfResultsAndConclusion || "-"}</TableCell>
                              <TableCell align='center'>{presentation.businessAndEngineeringContext || "-"}</TableCell>
                              <TableCell align='center'>{presentation.presentationQualityandDeliveryStyle || "-"}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : null}
            </>
          )}
        </Box>

        <Dialog fullWidth open={openScoreDialog} onClose={() => setOpenScoreDialog(false)} maxWidth="md">
          <DialogTitle sx={{ fontSize: 18, fontWeight: 600, }}>
            Score Submission for : <span style={{ fontWeight: 500 }}>{selectedPresentation?.title}</span>
          </DialogTitle>

          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {/* Row 1 - Presenter, Time Slot, Paper #, Organization */}
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={3}>
                <Typography variant="body1" gutterBottom sx={{ fontWeight: 600 }}>
                  Presenter : <span style={{ fontWeight: 500 }}>{selectedPresentation?.authorName}</span>
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body1" gutterBottom sx={{ fontWeight: 600 }}>
                  Time Slot : <span style={{ fontWeight: 500 }}>{selectedPresentation?.timeFrom} - {selectedPresentation?.timeTo}</span>
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body1" gutterBottom sx={{ fontWeight: 600 }}>
                  Organization : <span style={{ fontWeight: 500 }}>{selectedPresentation?.organization}</span>
                </Typography>
              </Grid>
            </Grid>
            {/* Row 2 - Title */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 18, fontWeight: 500 }} gutterBottom>
                  Scoring Criteria (1-10)
                </Typography>
              </Grid>
            </Grid>
            {/* Row 3 - Scoring fields */}
            <Grid container spacing={2} sx={{ mt: 3 }}>
              {[
                { label: "Business Value Discussion & Achievement (wg:x5)", key: "businessValueDiscussionAndAchievement" },
                { label: "Complexity of Technical Solutions (wg:x3)", key: "complexityOfTechnicalSolutions" },
                { label: "Interpretation of Results & Conclusion (wg:x3)", key: "interpretationOfResultsAndConclusion" },
                { label: "Business & Engineering Context (wg:x2)", key: "businessAndEngineeringContext" },
                { label: "Presentation Quality & Delivery Style(Optional) (wg:x3)", key: "presentationQualityandDeliveryStyle" }
              ].map((criteria) => (
                <Grid item xs={12} sm={6} md={4} key={criteria.key}>
                  <Typography sx={{ fontWeight: 600, mb: 1, fontSize: "0.9rem" }}>
                    {criteria.label}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={scores[criteria.key]}
                    onChange={(e) =>
                      setScores({
                        ...scores,
                        [criteria.key]: Math.min(10, Math.max(0, parseInt(e.target.value) || 0))
                      })
                    }
                    inputProps={{ min: 0, max: 10 }}
                  />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenScoreDialog(false)} disabled={loading} sx={{ fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              onClick={handleScoreSubmit}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, borderRadius: 16 }}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Submitting...' : 'Submit Scores'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openLogoutDialog} onClose={() => setOpenLogoutDialog(false)} maxWidth="xs" fullWidth>
          <DialogContent>
            <Typography sx={{ fontWeight: 600 }}>Are you sure you want to logout ?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenLogoutDialog(false)} sx={{ fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              onClick={handleLogoutConfirm}
              color="error"
              variant="outlined"
              sx={{ fontWeight: 600, borderRadius: 16 }}
            >
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default JudgeDashboard;